/**
 * Working Billing Claim Utilities
 *
 * Pure transform helpers for reading from and writing to the
 * WorkingBillingClaim structure on encounter responses.
 *
 * Naming is code-system-agnostic — `code_id` supports formats like
 * `CPT:99213`, `HCPCS:H2009`, etc.
 */

import type { WorkingClaimLineItem } from '@store/generated/encounters';
import { isValidCodeId, normalizeToCodeId, stripCodeSystem } from '@utils/billingCode';

import type {
  BillingCodeSource,
  BillingCodeSourceMap,
  WorkingBillingClaim,
  WorkingBillingClaimLineItem,
  WorkingBillingClaimPutBody,
} from '../types';

export {
  isValidCodeId,
  normalizeToCodeId,
  normalizeToCodeIdStrict,
  sanitizeBillingCodeInput,
  stripCodeSystem,
} from '@utils/billingCode';

/**
 * Extract bare billing code strings from a working billing claim's line items.
 * Strips the code system prefix (e.g. `CPT:99213` → `99213`).
 * Returns empty array if claim or line_items is null/undefined.
 */
export const extractBillingCodes = (claim: WorkingBillingClaim | null | undefined): string[] => {
  if (!claim?.line_items?.length) return [];
  return claim.line_items
    .map(li => (li.code_id ? stripCodeSystem(li.code_id) : ''))
    .filter(Boolean);
};

/**
 * Extract diagnosis codes from a working billing claim.
 * Returns empty array if claim or diagnosis_codes is null/undefined.
 */
export const extractDiagnosisCodes = (claim: WorkingBillingClaim | null | undefined): string[] => {
  return claim?.diagnosis_codes ?? [];
};

/**
 * Extract place of service from a working billing claim.
 * Returns null when the claim has no explicit POS.
 */
export const extractPlaceOfService = (
  claim: WorkingBillingClaim | null | undefined,
): string | null => {
  return claim?.place_of_service ?? null;
};

/**
 * Check if a claim has any non-empty line items.
 */
export const hasLineItems = (claim: WorkingBillingClaim | null | undefined): boolean => {
  return (claim?.line_items?.length ?? 0) > 0;
};

/**
 * Check if a claim has any non-empty diagnosis codes.
 */
export const hasDiagnosisCodes = (claim: WorkingBillingClaim | null | undefined): boolean => {
  return (claim?.diagnosis_codes?.length ?? 0) > 0;
};

/**
 * Return the display labels of line items whose code_id does not conform to
 * the strict backend write contract (see `isValidCodeId`). Blank codes are
 * reported as '(blank)'. An empty result means the list is safe to PUT.
 */
export const getNonConformingLineCodes = (
  items: ReadonlyArray<Pick<WorkingClaimLineItem, 'code_id'> | WorkingBillingClaimLineItem>,
): string[] =>
  items
    .filter(item => !isValidCodeId(item.code_id ?? ''))
    .map(item => item.code_id?.trim() || '(blank)');

/**
 * Return the existing NON-CONFORMING lines (legacy sentinels like `CPT:CASH`,
 * unsupported systems like `NDC:...`) that `representedItems` does not carry.
 *
 * Writers that rebuild `line_items` from bare code strings cannot represent
 * these lines (pickers often can't even display them). Appending the result
 * to the rebuilt list ensures an unrelated edit never silently omits a
 * persisted line from a full-replace PUT — which would permanently destroy
 * it (GDN-2658). The outbound write gate then blocks the PUT with an
 * actionable message instead of destroying data.
 */
export const getUnrepresentedNonConformingLines = (
  existingItems: readonly WorkingBillingClaimLineItem[] | null | undefined,
  representedItems: ReadonlyArray<Pick<WorkingClaimLineItem, 'code_id'>>,
): WorkingClaimLineItem[] => {
  const representedCodeIds = new Set(representedItems.map(item => item.code_id));
  return (existingItems ?? [])
    .filter(item => {
      const codeId = item.code_id?.trim();
      return Boolean(codeId) && !isValidCodeId(codeId ?? '') && !representedCodeIds.has(codeId ?? '');
    })
    .map(item => ({
      code_id: (item.code_id ?? '').trim(),
      source: item.source ?? 'provider_selected',
      modifiers: item.modifiers,
      modifier_sources: item.modifier_sources,
      units: item.units ?? undefined,
      charge_cents: item.charge_cents ?? undefined,
      diagnosis_codes: item.diagnosis_codes,
    }));
};

/**
 * Convert bare code strings to WorkingClaimLineItem[].
 *
 * Matches by bare code against existing items to preserve metadata
 * (modifiers, source). New codes get
 * `source: 'provider_selected'` and a canonical code-system prefix.
 *
 * Non-conforming existing lines that the bare-code list cannot represent are
 * appended unchanged rather than silently dropped — see
 * `getUnrepresentedNonConformingLines`.
 */
export const bareCodesToLineItems = (
  codes: string[],
  existingItems?: WorkingBillingClaimLineItem[],
): WorkingClaimLineItem[] => {
  const result: WorkingClaimLineItem[] = codes.map(bareCode => {
    // Try to find an existing line item that matches this bare code
    const existing = existingItems?.find(
      li => li.code_id && stripCodeSystem(li.code_id) === bareCode,
    );

    if (existing) {
      return {
        code_id: normalizeToCodeId(existing.code_id ?? bareCode),
        source: existing.source ?? 'provider_selected',
        modifiers: existing.modifiers,
        modifier_sources: existing.modifier_sources,
        units: existing.units ?? undefined,
        charge_cents: existing.charge_cents ?? undefined,
        diagnosis_codes: existing.diagnosis_codes,
      };
    }

    return {
      code_id: normalizeToCodeId(bareCode),
      source: 'provider_selected' as const,
    };
  });

  return [...result, ...getUnrepresentedNonConformingLines(existingItems, result)];
};

/**
 * Build a diagnosis_code_sources map from codes.
 *
 * Preserves provenance for codes that already exist in `existingSources`.
 * New codes are marked with `defaultSource` (defaults to 'provider_selected').
 */
export const buildDiagnosisCodeSources = (
  codes: string[],
  existingSources?: BillingCodeSourceMap | null,
  defaultSource: BillingCodeSource = 'provider_selected',
): BillingCodeSourceMap => {
  const sources: BillingCodeSourceMap = {};
  for (const code of codes) {
    sources[code] = existingSources?.[code] ?? defaultSource;
  }
  return sources;
};

/**
 * Best-effort count of line items the backend dropped from the read payload
 * because they could not be normalized.
 *
 * TODO(GDN-2657): the backend does not send a dropped-line signal yet. Once
 * GDN-2657 lands a `dropped_line_count` field on the working billing claim
 * response, regenerate the API types and read it directly. Until then this
 * reads the field defensively so the guards below activate the moment the
 * backend starts sending it.
 */
export const getDroppedLineCount = (claim: object | null | undefined): number => {
  const count = (claim as { dropped_line_count?: unknown } | null | undefined)
    ?.dropped_line_count;
  return typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : 0;
};

export type LineItemsWriteGateResult =
  | { allowed: true; lineItems: WorkingClaimLineItem[] }
  | { allowed: false; reason: string };

/**
 * Decide whether a `line_items` write is safe to send (GDN-2658).
 *
 * Blocks — never filters — when the outgoing list still carries
 * non-conforming codes (the backend would 422, and dropping the lines from
 * the payload would permanently destroy them on a full-replace PUT), or when
 * the backend reported dropped lines (the client is not rendering the whole
 * claim, so replacing `line_items` would delete the hidden lines).
 *
 * A blocked result means the caller should omit `line_items` from the PUT
 * (other fields may still be sent — the endpoint treats absent fields as
 * unchanged) and surface `reason` to the user.
 */
export const gateLineItemsWrite = (
  currentClaim: WorkingBillingClaim | null | undefined,
  lineItems: WorkingClaimLineItem[],
): LineItemsWriteGateResult => {
  const nonConforming = getNonConformingLineCodes(lineItems);
  if (nonConforming.length > 0) {
    const codes = [...new Set(nonConforming)].join(', ');
    return {
      allowed: false,
      reason: `Billing code changes weren't saved: ${codes} ${
        nonConforming.length === 1 ? 'is not a valid code' : 'are not valid codes'
      }. Remove or replace the invalid line to save code changes.`,
    };
  }

  const droppedCount = getDroppedLineCount(currentClaim);
  if (droppedCount > 0 && lineItems.length < (currentClaim?.line_items?.length ?? 0) + droppedCount) {
    return {
      allowed: false,
      reason:
        'Some billing lines could not be displayed, so code changes were not saved to avoid deleting them.',
    };
  }

  return { allowed: true, lineItems };
};

/**
 * Build a complete PUT request body from the current claim + partial updates.
 * Includes `expected_updated_at` from the claim for optimistic concurrency.
 *
 * This is a full-replace operation: fields not provided in `updates`
 * are carried forward from the current claim — except `line_items`, which is
 * OMITTED from the body when any persisted line is non-conforming. The
 * endpoint leaves absent fields untouched, so omitting both avoids echoing
 * legacy sentinels back to the strict validator (422 loop) and avoids
 * destroying the lines by resending a scrubbed list (GDN-2658).
 */
export const buildClaimPutRequest = (
  currentClaim: WorkingBillingClaim | null | undefined,
  updates: {
    lineItems?: WorkingClaimLineItem[];
    diagnosisCodes?: string[];
    diagnosisCodeSources?: BillingCodeSourceMap;
    placeOfService?: string | null;
  },
): WorkingBillingClaimPutBody => {
  const request: WorkingBillingClaimPutBody = {
    expected_updated_at: currentClaim?.updated_at ?? null,
  };

  // Line items: use update if provided, otherwise carry forward existing.
  // Carry-forward is skipped entirely when any persisted line is
  // non-conforming (resending it would trip the strict backend validator,
  // while scrubbing it from a full-replace PUT would destroy the line) OR
  // when the backend reported dropped lines (the visible list is truncated,
  // so a full-replace would delete the hidden lines). Omitting the field
  // leaves the server state untouched and still lets the other fields save.
  if (updates.lineItems !== undefined) {
    request.line_items = updates.lineItems;
  } else if (
    currentClaim?.line_items &&
    getNonConformingLineCodes(currentClaim.line_items).length === 0 &&
    getDroppedLineCount(currentClaim) === 0
  ) {
    request.line_items = currentClaim.line_items.map(li => ({
      code_id: li.code_id ?? '',
      source: li.source ?? 'provider_selected',
      modifiers: li.modifiers,
      modifier_sources: li.modifier_sources,
      units: li.units ?? undefined,
      charge_cents: li.charge_cents ?? undefined,
      diagnosis_codes: li.diagnosis_codes,
    }));
  }

  // Diagnosis codes: use update if provided, otherwise carry forward
  if (updates.diagnosisCodes !== undefined) {
    request.diagnosis_codes = updates.diagnosisCodes;
    if (updates.diagnosisCodeSources !== undefined) {
      request.diagnosis_code_sources = updates.diagnosisCodeSources;
    }
  } else if (currentClaim?.diagnosis_codes) {
    request.diagnosis_codes = currentClaim.diagnosis_codes;
    if (currentClaim.diagnosis_code_sources) {
      request.diagnosis_code_sources = currentClaim.diagnosis_code_sources;
    }
  }

  // Place of service: use update if provided, otherwise carry forward
  if (updates.placeOfService !== undefined) {
    request.place_of_service = updates.placeOfService;
  } else if (currentClaim?.place_of_service) {
    request.place_of_service = currentClaim.place_of_service;
  }

  return request;
};
